
import React from 'react';
import { Ticket, AppSettings, TicketStatus, PaymentStatus, RepairStageStatus, RepairStage } from '../types';
import { Button } from './Button';

interface CustomerPortalPageProps {
  ticket?: Ticket;
  appSettings: AppSettings;
  onMakePayment: (ticketId: string, amountDue: number, customerName: string) => void;
  onApproveEstimate?: (ticketId: string) => void;
  onDeclineEstimate?: (ticketId: string) => void;
  onNavigateBack: () => void;
}

// Icons
const PortalTicketIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-12 h-12 text-white" {...props}>
    <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
    <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
  </svg>
);
const CheckCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2" {...props}>
    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.06-1.06l-3.103 3.104-1.505-1.505a.75.75 0 00-1.06 1.061l2.25 2.25a.75.75 0 001.139-.076l3.5-4.025z" clipRule="evenodd" />
  </svg>
);
const XCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2" {...props}>
    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75 9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
  </svg>
);
const ClockIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2" {...props}>
    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75 9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
  </svg>
);
const CircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2.5 text-slate-400" {...props}>
    <circle cx="12" cy="12" r="10" />
  </svg>
);
const DotsCircleHorizontalIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => ( // Spinner-like for In Progress
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2.5 text-sky-500 animate-pulse" {...props}>
    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM8.25 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm3.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm3.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" clipRule="evenodd" />
  </svg>
);
const CheckIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => ( // For completed stages
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2.5 text-emerald-500" {...props}>
    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.06-1.06l-3.103 3.104-1.505-1.505a.75.75 0 00-1.06 1.061l2.25 2.25a.75.75 0 001.139-.076l3.5-4.025z" clipRule="evenodd" />
  </svg>
);
const MinusCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => ( // For skipped stages
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2.5 text-gray-400" {...props}>
    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75 9.75S17.385 2.25 12 2.25zm3 10.5a.75.75 0 000-1.5H9a.75.75 0 000 1.5h6z" clipRule="evenodd" />
  </svg>
);


const getStatusColorAndIcon = (status: TicketStatus): { color: string; icon: React.ReactNode; textColor: string } => {
  switch (status) {
    case TicketStatus.NEW: return { color: 'bg-sky-500', icon: <ClockIcon className="w-5 h-5 mr-2"/>, textColor: 'text-sky-700' };
    case TicketStatus.IN_PROGRESS: return { color: 'bg-amber-500', icon: <ClockIcon className="w-5 h-5 mr-2"/>, textColor: 'text-amber-700' };
    case TicketStatus.WAITING_FOR_PARTS: return { color: 'bg-purple-500', icon: <ClockIcon className="w-5 h-5 mr-2"/>, textColor: 'text-purple-700' };
    case TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL: return { color: 'bg-orange-500', icon: <ClockIcon className="w-5 h-5 mr-2"/>, textColor: 'text-orange-700' };
    case TicketStatus.READY_FOR_PICKUP: return { color: 'bg-[var(--color-accent)]', icon: <CheckCircleIcon className="w-5 h-5 mr-2"/>, textColor: 'text-[var(--color-accent)]' };
    case TicketStatus.COMPLETED: return { color: 'bg-emerald-500', icon: <CheckCircleIcon className="w-5 h-5 mr-2"/>, textColor: 'text-emerald-700' };
    case TicketStatus.CUSTOMER_DECLINED: return { color: 'bg-red-500', icon: <XCircleIcon className="w-5 h-5 mr-2"/>, textColor: 'text-red-700' };
    default: return { color: 'bg-slate-500', icon: <ClockIcon className="w-5 h-5 mr-2"/>, textColor: 'text-slate-700' };
  }
};

const getRepairStageIcon = (status: RepairStageStatus): React.ReactNode => {
  switch (status) {
    case RepairStageStatus.PENDING: return <CircleIcon />;
    case RepairStageStatus.IN_PROGRESS: return <DotsCircleHorizontalIcon />;
    case RepairStageStatus.COMPLETED: return <CheckIconSolid />;
    case RepairStageStatus.SKIPPED: return <MinusCircleIcon />;
    default: return <CircleIcon />;
  }
};

export const CustomerPortalPage: React.FC<CustomerPortalPageProps> = ({
  ticket,
  appSettings,
  onMakePayment,
  onApproveEstimate,
  onDeclineEstimate,
  onNavigateBack,
}) => {
  if (!ticket) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-6">
        <div className="bg-white p-8 rounded-lg shadow-xl text-center">
          {appSettings.logoBase64 && (
            <img src={appSettings.logoBase64} alt={`${appSettings.companyName} Logo`} className="mx-auto h-12 mb-6 object-contain" />
          )}
          <h2 className="text-2xl font-semibold text-red-600 mb-4">Ticket Not Found</h2>
          <p className="text-slate-600 mb-6">The requested ticket could not be loaded. It might have been removed or the ID is incorrect.</p>
          <Button variant="primary" onClick={onNavigateBack}>Go Back</Button>
        </div>
      </div>
    );
  }

  const { color: statusBgColor, icon: statusIcon, textColor: statusTextColor } = getStatusColorAndIcon(ticket.status);
  const canPay = (
        ticket.paymentStatus === PaymentStatus.UNPAID && ticket.amountDue > 0 &&
        (ticket.status === TicketStatus.READY_FOR_PICKUP || ticket.status === TicketStatus.COMPLETED || (ticket.status === TicketStatus.IN_PROGRESS && ticket.isEstimateApproved))
    );
  const canApproveDecline = ticket.status === TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL && onApproveEstimate && onDeclineEstimate;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col items-center p-4 sm:p-8">
      <header className="w-full max-w-4xl mb-8">
        <div className="bg-white p-4 rounded-lg shadow-lg flex justify-between items-center">
          <div className="flex items-center">
             {appSettings.logoBase64 ? (
                <img src={appSettings.logoBase64} alt={`${appSettings.companyName} Logo`} className="h-10 mr-3 object-contain" />
              ) : (
                <span className="text-xl sm:text-2xl font-bold text-[var(--color-primary)]">{appSettings.companyName}</span>
              )}
            <span className="text-xl sm:text-2xl font-semibold text-slate-700 hidden sm:inline-block ml-2">- Customer Portal</span>
          </div>
          <Button variant="ghost" onClick={onNavigateBack} className="text-sm">
            Exit Portal
          </Button>
        </div>
      </header>

      <main className="w-full max-w-4xl bg-white p-6 sm:p-8 rounded-xl shadow-2xl space-y-8">
        <div className="text-center border-b border-slate-200 pb-6 mb-6">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${statusBgColor} mb-4`}>
                 <PortalTicketIcon />
            </div>
            <h2 className="text-3xl font-bold text-slate-800">Repair Ticket #{ticket.id}</h2>
            <p className={`text-xl font-semibold mt-2 flex items-center justify-center ${statusTextColor}`}>
              {statusIcon} Current Status: {ticket.status}
            </p>
        </div>

        {/* Detailed Repair Progress */}
        <section>
          <h3 className="text-xl font-semibold text-slate-700 mb-4">Repair Progress</h3>
          <div className="space-y-3">
            {ticket.repairProgress?.map((stage: RepairStage, index: number) => (
              <div key={stage.id} className={`flex items-start p-3 rounded-md border ${
                stage.status === RepairStageStatus.COMPLETED ? 'border-emerald-200 bg-emerald-50' :
                stage.status === RepairStageStatus.IN_PROGRESS ? 'border-sky-200 bg-sky-50' :
                stage.status === RepairStageStatus.SKIPPED ? 'border-gray-200 bg-gray-50' :
                'border-slate-200 bg-slate-50'
              }`}>
                <div className="flex-shrink-0 mt-0.5">{getRepairStageIcon(stage.status)}</div>
                <div className="flex-grow">
                  <p className={`font-medium ${
                    stage.status === RepairStageStatus.COMPLETED ? 'text-emerald-700' :
                    stage.status === RepairStageStatus.IN_PROGRESS ? 'text-sky-700' :
                    stage.status === RepairStageStatus.SKIPPED ? 'text-gray-500 line-through' :
                    'text-slate-700'
                  }`}>{stage.name}</p>
                  {stage.status === RepairStageStatus.COMPLETED && stage.completedAt && (
                    <p className="text-xs text-slate-500">Completed: {formatDate(stage.completedAt)}</p>
                  )}
                   {stage.status === RepairStageStatus.IN_PROGRESS && stage.startedAt && (
                    <p className="text-xs text-slate-500">Started: {formatDate(stage.startedAt)}</p>
                  )}
                </div>
              </div>
            ))}
             {!ticket.repairProgress || ticket.repairProgress.length === 0 && (
                <p className="text-slate-500 text-sm">Detailed progress updates will appear here soon.</p>
            )}
          </div>
        </section>


        <section>
          <h3 className="text-xl font-semibold text-slate-700 mb-3">Device Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-slate-50 p-4 rounded-md">
            <p><strong>Device Model:</strong> {ticket.deviceModel || 'N/A'}</p>
            <p><strong>Serial Number:</strong> {ticket.deviceSerialNumber || 'N/A'}</p>
            <p className="md:col-span-2"><strong>Reported Issue:</strong> {ticket.subject}</p>
            {ticket.description && <p className="md:col-span-2"><strong>Details:</strong> {ticket.description}</p>}
          </div>
        </section>

        { (ticket.estimateDetails || ticket.status === TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL) && (
          <section>
            <h3 className="text-xl font-semibold text-slate-700 mb-3">Repair Estimate</h3>
            <div className="bg-slate-50 p-4 rounded-md space-y-3">
              <p className="text-sm whitespace-pre-wrap">{ticket.estimateDetails || 'Estimate details are being prepared.'}</p>
              <p className="text-lg font-semibold">Estimated Cost: <span className="text-[var(--color-primary)]">${ticket.amountDue.toFixed(2)}</span></p>
              {canApproveDecline && (
                <div className="flex flex-col sm:flex-row gap-3 pt-3 border-t border-slate-200">
                  <Button onClick={() => onApproveEstimate && onApproveEstimate(ticket.id)} variant="primary" className="flex-1">
                    <CheckCircleIcon className="text-white"/> Approve Estimate
                  </Button>
                  <Button onClick={() => onDeclineEstimate && onDeclineEstimate(ticket.id)} variant="danger" className="flex-1">
                     <XCircleIcon className="text-white"/> Decline Estimate
                  </Button>
                </div>
              )}
              {ticket.isEstimateApproved === true && ticket.status !== TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL && (
                <p className="text-emerald-600 font-semibold flex items-center"><CheckCircleIcon /> Estimate Approved</p>
              )}
              {ticket.isEstimateApproved === false && ticket.status !== TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL && (
                 <p className="text-red-600 font-semibold flex items-center"><XCircleIcon /> Estimate Declined</p>
              )}
            </div>
          </section>
        )}
        
        <section>
          <h3 className="text-xl font-semibold text-slate-700 mb-3">Payment Information</h3>
          <div className="bg-slate-50 p-4 rounded-md">
            {ticket.paymentStatus === PaymentStatus.PAID ? (
              <p className="text-2xl font-semibold text-emerald-600 flex items-center"><CheckCircleIcon /> Paid in Full</p>
            ) : (
              <>
                <p className="text-lg">Amount Due: <span className="font-bold text-red-600">${ticket.amountDue.toFixed(2)}</span></p>
                {canPay ? (
                  <Button 
                    onClick={() => onMakePayment(ticket.id, ticket.amountDue, ticket.customer)} 
                    variant="secondary" 
                    className="mt-3 w-full sm:w-auto"
                  >
                    Make Payment
                  </Button>
                ) : (
                  <p className="text-sm text-slate-500 mt-2">
                    {ticket.status === TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL 
                      ? "Payment can be made after estimate approval." 
                      : "Payment will be available once the repair is ready or completed."}
                  </p>
                )}
              </>
            )}
          </div>
        </section>
        
        <footer className="text-center text-xs text-slate-500 pt-6 border-t border-slate-200">
            <p>Thank you for choosing {appSettings.companyName}.</p>
            <p>If you have any questions, please contact us at {appSettings.companyPhone}.</p>
            <p>&copy; {new Date().getFullYear()} {appSettings.companyName}. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
};
